import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Search, X } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { recoverRequestsApi, type ScenarioDependency } from '../api'

/**
 * What a recovery scenario depends on to work at all (Andrew, 14 Oct 2026: "Add depends on to
 * recovery scenarios").
 *
 * Not what an attack might hit - "it gets very specific and an attack is unknown" - but what the
 * scenario itself needs intact: the place it recovers into, the identity provider, a platform.
 * It is written once, when the scenario is, and an impact assessment then rules the scenario out
 * when one of these is compromised and says which.
 *
 * Choose a kind, search, click to add; a chip is removed with its cross.
 */
type Kind = ScenarioDependency['kind']
const KINDS: Kind[] = ['location', 'technology', 'object']
const SHOWN = 8

export function ScenarioDependsOn({ value, onChange, enabled }: { value: ScenarioDependency[]; onChange: (next: ScenarioDependency[]) => void; enabled: boolean }) {
    const { t } = useTranslation()
    const [kind, setKind] = useState<Kind>('location')
    const [search, setSearch] = useState('')
    const { data: options } = useQuery({
        queryKey: ['recover-requests', 'scenarios', 'dependency-options'],
        queryFn: async () => (await recoverRequestsApi.scenarioDependencyOptions()).data.data,
        enabled,
    })
    const kindLabel = (k: Kind) => (k === 'location' ? t('recoveryScenarios.dependsOn.location', 'Location')
        : k === 'technology' ? t('recoveryScenarios.dependsOn.platform', 'Platform')
            : t('recoveryScenarios.dependsOn.object', 'Object or service'))
    const has = (k: Kind, refId: string) => value.some((d) => d.kind === k && d.refId === refId)
    const matches = useMemo(() => {
        const words = search.trim().toLowerCase()
        return (options?.[kind] ?? []).filter((o) => !has(kind, o.refId) && (!words || `${o.name} ${o.refId}`.toLowerCase().includes(words)))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, kind, search, value])

    return (
        <div className="space-y-2" data-testid="scenario-depends-on">
            <Label>{t('recoveryScenarios.dependsOn.title', 'Depends on')}</Label>
            <p className="text-xs text-muted-foreground">{t('recoveryScenarios.dependsOn.help', 'What this scenario needs intact to work at all: the place it recovers into, the identity provider, a platform. If an impact assessment finds one of these compromised, it says this scenario cannot be used.')}</p>
            {value.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                    {value.map((d) => (
                        <li key={`${d.kind}:${d.refId}`} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 px-2 py-0.5 text-xs">
                            <span className="text-muted-foreground">{kindLabel(d.kind)}:</span>
                            <span className="font-medium">{d.name}</span>
                            {d.exists === false && <span className="text-destructive">{t('recoveryScenarios.dependsOn.gone', '(no longer exists)')}</span>}
                            <button type="button" className="rounded-full p-0.5 hover:bg-muted" aria-label={t('recoveryScenarios.dependsOn.remove', 'Remove {{name}}', { name: d.name })} onClick={() => onChange(value.filter((x) => !(x.kind === d.kind && x.refId === d.refId)))}>
                                <X className="h-3 w-3" />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <div className="flex gap-2">
                <Select value={kind} onValueChange={(next) => { setKind(next as Kind); setSearch('') }}>
                    <SelectTrigger className="h-9 w-44" aria-label={t('recoveryScenarios.dependsOn.kind', 'Kind of thing it depends on')}><SelectValue /></SelectTrigger>
                    <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{kindLabel(k)}</SelectItem>)}</SelectContent>
                </Select>
                <label className="relative min-w-0 flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} aria-label={t('recoveryScenarios.dependsOn.search', 'Search for something to add')} placeholder={t('common.search', 'Search')} className="h-9 w-full rounded-md border bg-background pl-8 pr-2 text-sm" />
                </label>
            </div>
            <ul className="max-h-40 overflow-y-auto rounded-md border text-sm" data-testid="scenario-depends-on-options">
                {matches.slice(0, SHOWN).map((o) => (
                    <li key={o.refId}>
                        <button type="button" className="flex w-full items-baseline gap-2 px-2 py-1 text-left hover:bg-muted" onClick={() => onChange([...value, { kind, refId: o.refId, name: o.name }])}>
                            <span className="truncate">{o.name}</span>
                            {o.name !== o.refId && kind === 'object' && <span className="flex-shrink-0 text-xs text-muted-foreground">{o.refId}</span>}
                        </button>
                    </li>
                ))}
                {matches.length === 0 && <li className="px-2 py-1 text-xs text-muted-foreground">{options ? t('recoveryScenarios.dependsOn.none', 'Nothing to add matches.') : t('common.loading', 'Loading...')}</li>}
                {matches.length > SHOWN && <li className="px-2 py-1 text-xs text-muted-foreground">{t('recoveryScenarios.dependsOn.more', '{{count}} more: search to narrow it.', { count: matches.length - SHOWN })}</li>}
            </ul>
        </div>
    )
}
